const VIDEO_MIME_TYPES = ['video/mp4']

const isVideoType = file => {
  const fileType = (file?.type || '').toLowerCase()
  return VIDEO_MIME_TYPES.includes(fileType)
}

const loadVideo = (src, {crossOrigin} = {}) =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.muted = true
    video.playsInline = true
    if (crossOrigin) {
      video.crossOrigin = crossOrigin
    }

    video.onloadeddata = () => resolve(video)
    video.onerror = () => reject(new Error('Failed to load video for thumbnail generation.'))

    video.src = src
  })

const canvasToBlob = (canvas, outputType, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Failed to render thumbnail blob.'))
          return
        }

        resolve(blob)
      },
      outputType,
      quality
    )
  })

const getScaledDimensions = (width, height, maxWidth, maxHeight) => {
  if (!width || !height) {
    return {width: maxWidth, height: maxHeight}
  }

  const ratio = Math.min(maxWidth / width, maxHeight / height, 1)
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  }
}

const drawPlayOverlay = (context, width, height) => {
  const centerX = width / 2
  const centerY = height / 2
  const radius = Math.max(14, Math.min(width, height) * 0.14)

  context.save()

  // Dark circular badge for better contrast over bright frames.
  context.fillStyle = 'rgba(0, 0, 0, 0.45)'
  context.beginPath()
  context.arc(centerX, centerY, radius, 0, Math.PI * 2)
  context.fill()

  // White play triangle centered in the badge.
  const triangleWidth = radius * 0.9
  const triangleHeight = radius * 1.1
  const leftX = centerX - triangleWidth * 0.32
  const topY = centerY - triangleHeight / 2

  context.fillStyle = '#ffffff'
  context.beginPath()
  context.moveTo(leftX, topY)
  context.lineTo(leftX, topY + triangleHeight)
  context.lineTo(leftX + triangleWidth, centerY)
  context.closePath()
  context.fill()

  context.restore()
}

const renderFrameToThumbnailUrl = async ({
  sourceWidth,
  sourceHeight,
  drawFrame,
  outputType,
  quality,
  maxWidth,
  maxHeight,
  includePlayIcon = false,
}) => {
  const {width, height} = getScaledDimensions(sourceWidth, sourceHeight, maxWidth, maxHeight)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  if (!context) throw new Error('Unable to create canvas context for thumbnail generation.')

  drawFrame(context, width, height)

  if (includePlayIcon) {
    drawPlayOverlay(context, width, height)
  }

  const blob = await canvasToBlob(canvas, outputType, quality)
  return URL.createObjectURL(blob)
}

const seekVideo = (video, currentTime) =>
  new Promise((resolve, reject) => {
    const handleSeeked = () => {
      cleanup()
      resolve()
    }

    const handleError = () => {
      cleanup()
      reject(new Error('Failed to seek video frame for thumbnail generation.'))
    }

    const cleanup = () => {
      video.removeEventListener('seeked', handleSeeked)
      video.removeEventListener('error', handleError)
    }

    video.addEventListener('seeked', handleSeeked)
    video.addEventListener('error', handleError)
    video.currentTime = currentTime
  })

const generateMp4Thumbnail = async (file, options) => {
  const sourceUrl = URL.createObjectURL(file)

  try {
    const video = await loadVideo(sourceUrl)
    const targetTime = Math.min(options.captureAtSecond, Math.max(0, (video.duration || 0) / 2))
    await seekVideo(video, targetTime)

    return await renderFrameToThumbnailUrl({
      sourceWidth: video.videoWidth,
      sourceHeight: video.videoHeight,
      drawFrame: (context, width, height) => context.drawImage(video, 0, 0, width, height),
      outputType: options.outputType,
      quality: options.quality,
      maxWidth: options.maxWidth,
      maxHeight: options.maxHeight,
      includePlayIcon: options.includePlayIcon,
    })
  } finally {
    URL.revokeObjectURL(sourceUrl)
  }
}

const isRemoteUrl = url => typeof url === 'string' && !url.startsWith('blob:') && !url.startsWith('data:')

const generateMp4ThumbnailFromUrl = async (videoUrl, options) => {
  if (!videoUrl) {
    throw new Error('Video URL is required for thumbnail generation.')
  }

  const video = await loadVideo(videoUrl, isRemoteUrl(videoUrl) ? {crossOrigin: 'anonymous'} : {})
  const targetTime = Math.min(options.captureAtSecond, Math.max(0, (video.duration || 0) / 2))
  await seekVideo(video, targetTime)

  return renderFrameToThumbnailUrl({
    sourceWidth: video.videoWidth,
    sourceHeight: video.videoHeight,
    drawFrame: (context, width, height) => context.drawImage(video, 0, 0, width, height),
    outputType: options.outputType,
    quality: options.quality,
    maxWidth: options.maxWidth,
    maxHeight: options.maxHeight,
    includePlayIcon: options.includePlayIcon,
  })
}

export const generateMediaThumbnail = async (
  file,
  {
    maxWidth = 320,
    maxHeight = 180,
    outputType = 'image/jpeg',
    quality = 0.85,
    captureAtSecond = 0.15,
    includePlayIcon = true,
  } = {}
) => {
  if (!file) throw new Error('File is required for thumbnail generation.')
  if (!isVideoType(file)) {
    throw new Error('Thumbnail generation supports only MP4 files.')
  }

  const options = {maxWidth, maxHeight, outputType, quality, captureAtSecond, includePlayIcon}

  return generateMp4Thumbnail(file, options)
}

export const generateVideoThumbnail = async (file, options) => {
  return generateMediaThumbnail(file, options)
}

export const generateVideoThumbnailFromUrl = async (videoUrl, options) => {
  const resolvedOptions = {
    maxWidth: 320,
    maxHeight: 180,
    outputType: 'image/jpeg',
    quality: 0.85,
    captureAtSecond: 0.15,
    includePlayIcon: true,
    ...(options || {}),
  }

  return generateMp4ThumbnailFromUrl(videoUrl, resolvedOptions)
}

export const revokeThumbnailUrl = thumbnailUrl => {
  if (!thumbnailUrl) return
  URL.revokeObjectURL(thumbnailUrl)
}
